import { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { useLocation } from "react-router-dom";

/**
 * R109.24 — Floating WhatsApp chat bubble.
 *
 * A persistent bottom-right FAB linking to wa.me/918130617987 with a
 * pre-filled greeting. Hidden on admin routes and on the payment-return
 * page (the return page needs the entire viewport for the redirect banner).
 *
 * Behavior:
 *   - After 4s, expands to show a tooltip "Chat with us on WhatsApp".
 *   - Tooltip is dismissible; user's choice is remembered in localStorage
 *     so we don't nag on every page load.
 *   - `data-testid="floating-whatsapp-btn"` for tests.
 */
const WA_URL = "https://wa.me/918130617987?text=Hi%20VentureKhoj%20team%2C%20I%20need%20help";
const DISMISS_KEY = "vk_wa_tooltip_dismissed_v1";
const HIDE_ON_PREFIXES = ["/admin", "/payment/return", "/onboarding", "/login", "/signup", "/paywall", "/pricing", "/plans", "/checkout"];
// R110.130 — Suppress the auto-expanding tooltip on pages that have a
// bottom-sticky CTA bar (recommendations sticky Connect bar) so the two
// don't visually collide. The FAB itself is still available.
const HIDE_TOOLTIP_ON_PREFIXES = ["/recommendations", "/blog/"];

// R221 — Any bottom-sticky CTA bar that shares the viewport's bottom edge with
// the FAB tooltip. If one is visible, the auto-tooltip is suppressed so the two
// never collide (the FAB itself stays available).
const BOTTOM_BAR_SEL = [
  "[data-testid='seo-sticky-lead-bar']",
  "[data-testid='seo-sticky-brand-bar']",
  "[data-testid='venture-plan-sticky-bar']",
  "[data-testid='venture-mobile-cta-bar']",
  "[data-testid='franchise-bulk-bar']",
  "[data-testid='compare-sticky-bar']",
  "[data-testid='kundali-mobile-cta-bar']",
  "[data-testid='kk-home-mobile-cta-bar']",
  "[data-testid='home-cta-bar']",
].join(",");

// True only when a bar is actually on-screen (not translated off / zero-size).
const bottomBarVisible = () => {
  if (typeof document === "undefined") return false;
  const h = window.innerHeight || 0;
  return [...document.querySelectorAll(BOTTOM_BAR_SEL)].some((el) => {
    const r = el.getBoundingClientRect();
    return r.height > 0 && r.top < h - 4 && r.bottom > 0;
  });
};

export default function FloatingWhatsApp() {
  const loc = useLocation();
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try { dismissed = !!localStorage.getItem(DISMISS_KEY); } catch { /* ignore */ }
    if (dismissed) return;
    // R110.130 — On pages that host a bottom-sticky CTA bar, skip the
    // auto-expanding tooltip so it doesn't fight the CTA for attention.
    if (HIDE_TOOLTIP_ON_PREFIXES.some((p) => loc.pathname.startsWith(p))) return;
    const id = setTimeout(() => {
      if (bottomBarVisible()) return;
      setShowTip(true);
    }, 4000);
    return () => clearTimeout(id);
  }, [loc.pathname]);

  // R221 — A sticky CTA bar can appear AFTER the 4s tooltip (on scroll or when
  // the user selects items). Hide the tooltip the moment any bar becomes
  // visible so they don't overlap on laptop/desktop.
  useEffect(() => {
    if (!showTip) return;
    const check = () => { if (bottomBarVisible()) setShowTip(false); };
    check();
    const iv = setInterval(check, 600);
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      clearInterval(iv);
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [showTip]);

  const shouldHide = HIDE_ON_PREFIXES.some((p) => loc.pathname.startsWith(p));
  if (shouldHide) return null;

  // R110.131 / R214 — City detail pages already surface the primary CTAs via
  // a bottom-sticky two-CTA bar on mobile AND inline "Browse All" / "Post Your
  // Query" buttons in the hero. Hide the FAB entirely on mobile for those
  // pages so it stops covering the hero Browse-All button; restore the normal
  // corner position at lg+ (sticky bar hidden there).
  const onCityBar = /^\/business-ideas\/location\/[^/]+$/.test(loc.pathname);
  const posClass = onCityBar
    ? "hidden lg:flex bottom-5 right-5 lg:bottom-6 lg:right-6"
    : "bottom-5 right-5 sm:bottom-6 sm:right-6";

  const dismissTip = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowTip(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  // R211 / R216 — On blog-post AND public-venture (idea) pages a fixed bottom
  // CTA bar owns the mobile bottom edge; lift the FAB above it on < lg so the
  // two don't overlap. `/venture/:slug` only (not /venture/:slug/feasibility).
  const onBlogPost = /^\/blog\/[^/]+/.test(loc.pathname)
    && !/^\/blog\/(category|tag|author)\//.test(loc.pathname);
  const onVentureBar = /^\/venture\/[^/]+$/.test(loc.pathname);
  const onKundaliSeo = /^\/kundali\/[^/]+$/.test(loc.pathname);
  const onKundaliHome = loc.pathname === "/kundali-khoj";
  // R222 — Home page hosts a mobile-only 3-CTA bottom bar; lift the FAB above
  // it on < md (the bar is hidden at md+, so the FAB returns to the corner).
  const onHome = loc.pathname === "/";
  const posCls = onHome
    ? "fixed bottom-20 right-5 z-[60] flex items-end gap-2 md:bottom-6 md:right-6"
    : (onBlogPost || onVentureBar || onKundaliSeo || onKundaliHome)
    ? "fixed bottom-20 right-5 z-[60] flex items-end gap-2 lg:bottom-6 lg:right-6"
    : (onCityBar
        ? `fixed z-[60] items-end gap-2 ${posClass}`
        : `fixed z-[60] flex items-end gap-2 ${posClass}`);

  return (
    <div className={posCls}>
      {showTip && (
        <div
          data-testid="floating-whatsapp-tip"
          className="hidden sm:flex items-center gap-2 rounded-full bg-white border border-slate-200 shadow-lg pl-4 pr-2 py-2 text-sm text-slate-800 max-w-xs animate-in fade-in slide-in-from-right-4"
          role="status"
        >
          <span>Need help? Chat with us on WhatsApp</span>
          <button
            onClick={dismissTip}
            aria-label="Dismiss"
            className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
            data-testid="floating-whatsapp-tip-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <a
        href={WA_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        data-testid="floating-whatsapp-btn"
        onClick={() => {
          // Track intent so we can measure inbound uplift later.
          try {
            if (typeof window !== "undefined" && window.gtag) {
              window.gtag("event", "whatsapp_click", { source: "floating_bubble", path: loc.pathname });
            }
            if (typeof window !== "undefined" && window.fbq) {
              window.fbq("trackCustom", "WhatsAppClick", { path: loc.pathname });
            }
          } catch { /* ignore */ }
        }}
        className="group relative flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/40 hover:bg-emerald-600 hover:shadow-emerald-500/60 active:scale-95 transition-all"
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-emerald-500/40 animate-ping pointer-events-none" aria-hidden="true" />
        {/* WhatsApp glyph */}
        <svg viewBox="0 0 24 24" className="h-7 w-7 relative" fill="currentColor" aria-hidden="true">
          <path d="M20.52 3.48A11.87 11.87 0 0 0 12.01 0C5.39 0 .01 5.39.01 12.01c0 2.12.55 4.19 1.6 6.02L0 24l6.15-1.61a11.98 11.98 0 0 0 5.86 1.5h.01c6.62 0 12-5.39 12-12.01a11.9 11.9 0 0 0-3.5-8.4Zm-8.51 18.4h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.65.96.98-3.56-.24-.37a9.83 9.83 0 0 1-1.5-5.31c0-5.44 4.43-9.87 9.88-9.87 2.64 0 5.11 1.03 6.97 2.9a9.79 9.79 0 0 1 2.9 6.98c-.01 5.44-4.44 9.86-9.88 9.86Zm5.42-7.39c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.93 1.17-.17.2-.34.22-.63.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.34.44-.51.15-.17.2-.29.3-.49.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.19-.24-.58-.48-.5-.66-.51h-.56c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.87 1.21 3.07c.15.2 2.09 3.18 5.06 4.47.71.31 1.26.49 1.7.63.71.23 1.36.19 1.87.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35Z" />
        </svg>
        {/* Fallback tooltip on desktop hover when tip is dismissed */}
        <span className="pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
          WhatsApp us
        </span>
      </a>
    </div>
  );
}
